import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { catchError, from, of, tap, throwError } from 'rxjs';

import {
  OfflineQueueService,
  isNetworkError,
  isQueueableRequest,
} from './offline-queue.service';
import { OfflineService } from './offline.service';
import {
  SKIP_OFFLINE_GET_CACHE_FALLBACK,
  SKIP_OFFLINE_GET_CACHE_WRITE,
} from './offline-http.context';

function isCheckTokenValidityRequest(url: string): boolean {
  return url.includes('/rpc/check_token_validity');
}

function isGetRequest(request: { method?: string }): boolean {
  return request.method?.toUpperCase() === 'GET';
}

/**
 * Offline handling:
 * - Caches successful GET responses for use when offline
 * - GET + network error: returns cached response if available
 * - check_token_validity + network error: assumes logged in ({ loggedin: true })
 * - POST/PUT/PATCH/DELETE (queueable) + network error: queues and throws RequestQueuedOfflineError
 */
export const offlineQueueInterceptor: HttpInterceptorFn = (request, next) => {
  const queue = inject(OfflineQueueService);
  const offlineService = inject(OfflineService);

  return next(request).pipe(
    tap((event) => {
      if (
        event instanceof HttpResponse &&
        isGetRequest(request) &&
        !request.context.get(SKIP_OFFLINE_GET_CACHE_WRITE)
      ) {
        offlineService.setCachedResponse(request.url, event.body);
      }
    }),
    catchError((err) => {
      if (!isNetworkError(err)) {
        return throwError(() => err);
      }
      // TODO: check if this is ok. it should be because check token is only checked if we are already logged in.
      if (isCheckTokenValidityRequest(request.url)) {
        return of(new HttpResponse({ body: { loggedin: true }, status: 200 }));
      }
      if (isGetRequest(request)) {
        if (request.context.get(SKIP_OFFLINE_GET_CACHE_FALLBACK)) {
          return throwError(() => err);
        }
        return from(
          offlineService.getCachedResponse(request.url).then((cached) => {
            if (cached?.response != null) {
              return new HttpResponse({ body: cached.response, status: 203 });
            }
            throw err;
          })
        );
      }
      if (isQueueableRequest(request)) {
        return from(queue.enqueue(request));
      }
      return throwError(() => err);
    })
  );
};
